import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import {
  ADMIN_MODULES,
  canAccessModule,
  isSuperAdminUser,
} from "../../helper/permissions";

const Sidebar = () => {
  const [isVisible, setIsVisible] = useState(true);
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const officeChildPaths = useMemo(() => {
    const group = ADMIN_MODULES.find((m) => m.key === "offices-group");
    return (group?.children || []).map((c) => c.path);
  }, []);

  const sdcChildPaths = useMemo(() => {
    const group = ADMIN_MODULES.find((m) => m.key === "sdc-group");
    return (group?.children || []).map((c) => c.path);
  }, []);

  const announcementsChildPaths = useMemo(() => {
    const group = ADMIN_MODULES.find((m) => m.key === "announcements-group");
    return (group?.children || []).map((c) => c.path);
  }, []);

  const officesOpenByDefault = officeChildPaths.some(
    (p) => location.pathname === p
  );
  const sdcOpenByDefault = sdcChildPaths.some((p) => location.pathname === p);
  const announcementsOpenByDefault = announcementsChildPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith("/announcements/")
  );
  const [officesOpen, setOfficesOpen] = useState(officesOpenByDefault);
  const [sdcOpen, setSdcOpen] = useState(sdcOpenByDefault);
  const [announcementsOpen, setAnnouncementsOpen] = useState(
    announcementsOpenByDefault
  );

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    dispatch(logout());
    navigate("/signin");
  };

  const linkStyle = (path) => ({
    display: "block",
    padding: "12px 16px",
    margin: "6px 0",
    borderRadius: "6px",
    textDecoration: "none",
    color: isActive(path) ? "#fff" : "rgba(255, 255, 255, 0.9)",
    background: isActive(path) ? "green" : "rgba(255, 255, 255, 0.1)",
    transition: "background 0.3s ease, transform 0.2s ease",
  });

  const childLinkStyle = (path) => ({
    ...linkStyle(path),
    padding: "10px 14px",
    margin: "4px 0",
    fontSize: 14,
  });

  const visibleModules = ADMIN_MODULES.map((item) => {
    if (Array.isArray(item.children) && item.children.length) {
      const children = item.children.filter((child) =>
        canAccessModule(user, child.key)
      );
      if (!children.length) return null;
      return { ...item, children };
    }
    if (!canAccessModule(user, item.key)) return null;
    return item;
  }).filter(Boolean);

  const showUsers = isSuperAdminUser(user) || !user;

  const roleLabel = isSuperAdminUser(user)
    ? "Super Admin"
    : user?.role === "admin"
      ? "Admin"
      : user?.role === "limited"
        ? "Limited Access"
        : "Admin";

  return (
    <aside
      className="sidebarResponsive"
      style={{
        background: "linear-gradient(135deg, #4a90e2, #6C63FF)",
        color: "#fff",
        padding: "20px",
        boxShadow: "2px 0 8px rgba(0, 0, 0, 0.15)",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        height: "100vh",
        position: "sticky",
        top: 0,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexShrink: 0,
          marginBottom: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>{roleLabel}</h2>
          <h4 style={{ margin: "4px 0 0", fontWeight: 500 }}>University of Okara</h4>
          {user?.email && (
            <p style={{ margin: "8px 0 0", fontSize: 12, opacity: 0.85 }}>{user.email}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsVisible((prev) => !prev)}
          className={isVisible ? "change" : ""}
          aria-label="Toggle menu"
          style={{
            width: 40,
            height: 40,
            margin: 0,
            padding: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(0,0,0,0.2)",
            border: 0,
            borderRadius: 6,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <div>
            <div className="bar1" />
            <div className="bar2" />
            <div className="bar3" />
          </div>
        </button>
      </div>

      {isVisible && (
        <nav
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          {visibleModules.map((item) => {
            if (item.children?.length) {
              const groupActive = item.children.some(
                (c) =>
                  isActive(c.path) ||
                  (item.key === "announcements-group" &&
                    location.pathname.startsWith("/announcements/"))
              );
              const open =
                item.key === "offices-group"
                  ? officesOpen || groupActive
                  : item.key === "sdc-group"
                    ? sdcOpen || groupActive
                    : item.key === "announcements-group"
                      ? announcementsOpen || groupActive
                      : true;

              return (
                <div key={item.key} style={{ margin: "6px 0" }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (item.key === "offices-group") {
                        setOfficesOpen((prev) => !prev);
                      } else if (item.key === "sdc-group") {
                        setSdcOpen((prev) => !prev);
                      } else if (item.key === "announcements-group") {
                        setAnnouncementsOpen((prev) => !prev);
                      }
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderRadius: 6,
                      border: 0,
                      cursor: "pointer",
                      color: "#fff",
                      background: groupActive
                        ? "rgba(255,255,255,0.18)"
                        : "rgba(255, 255, 255, 0.1)",
                      fontSize: 16,
                      textAlign: "left",
                    }}
                  >
                    <span>{item.label}</span>
                    <span style={{ opacity: 0.85 }}>{open ? "▾" : "▸"}</span>
                  </button>
                  {open && (
                    <div style={{ paddingLeft: 10, marginTop: 2 }}>
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          style={childLinkStyle(child.path)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link key={item.path} to={item.path} style={linkStyle(item.path)}>
                {item.label}
              </Link>
            );
          })}

          {showUsers && (
            <Link to="/users" style={linkStyle("/users")}>
              Users
            </Link>
          )}
        </nav>
      )}

      <div
        style={{
          flexShrink: 0,
          marginTop: 12,
          paddingTop: 12,
          borderTop: "1px solid rgba(255,255,255,0.2)",
        }}
      >
        <button
          type="button"
          onClick={handleLogout}
          style={{
            width: "100%",
            padding: "12px 16px",
            backgroundColor: "#FF6B6B",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          Logout
        </button>
        <p
          style={{
            fontSize: "12px",
            marginTop: "10px",
            marginBottom: 0,
            color: "rgba(255, 255, 255, 0.7)",
          }}
        >
          © 2026 UO | Powered by Ezeeta
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
